import cn from 'clsx'
import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import styled from 'styled-components'

import { CustomModalConfig } from '..'

import * as Analytics from 'analytics/analytics'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import LineUniversityIcon from 'components/Luxkit/Icons/line/LineUniversityIcon'
import ListItem from 'components/Luxkit/List/ListItem'
import Heading from 'components/Luxkit/Typography/Heading'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { useScreenSizeBetween } from 'hooks/useScreenSize'
import zIndex from 'styles/tools/z-index'
import MapSearchAutocomplete, {
  CHARACTER_THRESHOLD,
} from 'tripPlanner/components/AutocompleteInputs/MapSearchAutoComplete'
import SearchBookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/Search/SearchBookmarkButton'
import MapSearchPresentationSelector from 'tripPlanner/components/Search/MapSearchPresentationSelector'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { useExploreItems } from 'tripPlanner/hooks/api/explore'
import {
  ExploreCategory,
  formatExploreQuery,
} from 'tripPlanner/pages/trip/Explore/common'
import { ExploreItem } from 'tripPlanner/types/common'
import { AutocompletePlaceParam } from 'tripPlanner/types/google'
import { AccommodationItem } from 'tripPlanner/types/tripItem'
import { TripGooglePlaceAutocompletePrediction } from 'tripPlanner/api/places/types'

const SearchBarContainer = styled.div`
  z-index: ${zIndex.header};
  position: absolute;
  width: calc(100% - ${rem(40)});
  margin-top: ${rem(24)};
  margin-right: ${rem(20)};
  margin-left: ${rem(20)};

  &.full-screen {
    height: 100%;
    background-color: white;
    width: 100%;
    margin: 0;
    padding-top: ${rem(24)};
    padding-right: ${rem(20)};
    padding-left: ${rem(20)};
  }
`

const ExploreDropDownContainer = styled.div`
  margin-top: ${rem(8)};

  &:not(.full-screen) {
    padding: ${rem(16)};
    box-shadow: ${(props) => props.theme.shadow.bottom.small};
    border: 1px solid ${(props) => props.theme.palette.neutral.default.six};
    border-radius: ${(props) => props.theme.borderRadius.S};
    background-color: ${(props) => props.theme.palette.neutral.default.eight};
  }
`

const StyledHeading = styled(Heading)`
  padding-bottom: ${rem(8)};
`

interface Props {
  mapRef: React.MutableRefObject<google.maps.Map | undefined>
  tripId: string
  updateModalConfig?: (value: CustomModalConfig) => void
  setMapSearchItem: (marker: ExploreItem | undefined) => void
}

const AUTOCOMPLETE_TYPES: AutocompletePlaceParam = []
const LOCATION_BIAS_ZOOM_LEVEL = 8
const DEFAULT_CATEGORY: ExploreCategory = {
  label: 'Top attractions',
} as ExploreCategory

function MapSearch(props: Props) {
  const { mapRef, tripId, updateModalConfig, setMapSearchItem } = props
  const methods = useForm({
    defaultValues: {
      place: undefined,
    },
  })
  const { register } = methods
  const [searchPhrase, setSearchPhrase] = useState('')

  const currentZoom = mapRef.current?.getZoom()

  const [searchLocationBias, setSearchLocationBias] = useState<
    undefined | { lat: number; lng: number }
  >(undefined)

  useEffect(() => {
    const mapCenter = mapRef.current?.getCenter()

    if (
      currentZoom &&
      currentZoom >= LOCATION_BIAS_ZOOM_LEVEL &&
      mapCenter?.lat() &&
      mapCenter?.lng()
    ) {
      // https://stackoverflow.com/questions/54690118/why-is-google-maps-getcenter-returning-invalid-coordinates
      const unWrappedLatLng = new google.maps.LatLng(
        mapCenter.lat(),
        mapCenter.lng(),
      )
      setSearchLocationBias({
        lat: unWrappedLatLng.lat(),
        lng: unWrappedLatLng.lng(),
      })
    } else {
      // if we're zoomed out enough, we don't want a bias on our results.
      setSearchLocationBias(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentZoom, mapRef?.current?.getCenter()])

  const { data: trip } = useTrip({ tripId })

  const tripPlace = useMemo(
    () => trip?.destinationsGeo?.[0],
    [trip?.destinationsGeo],
  )

  const accommodationItems = useMemo(
    () =>
      (trip?.items.filter(
        (item) => item.type === 'ACCOMMODATION' && 'point' in item.place,
      ) as Array<AccommodationItem>) || [],
    [trip?.items],
  )

  const inputRef = React.useRef<HTMLInputElement>(null)

  const searchStarted = searchPhrase.length > CHARACTER_THRESHOLD

  const [exploreQuery, setExploreQuery] = useState<string | undefined>(
    undefined,
  )
  const [exploreLocationBias, setExploreLocationBias] = useState<
    [number, number] | undefined
  >(undefined)

  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (tripPlace) {
      setExploreQuery(formatExploreQuery(tripPlace, DEFAULT_CATEGORY))
      setExploreLocationBias([tripPlace.point.lat, tripPlace.point.lng])
    }
  }, [tripPlace])

  const { data: exploreItems } = useExploreItems({
    query: exploreQuery,
    locationBias: exploreLocationBias,
  })

  const isTabletOrSmaller = useScreenSizeBetween('mobile', 'tablet') // full screen map search mode

  useEffect(() => {
    if (isTabletOrSmaller) {
      if (isFocused) {
        updateModalConfig?.({
          title: 'What are you looking for?',
          closeModalAction: () => {
            setIsFocused(false)
          },
        })
      } else {
        updateModalConfig?.({})
        setIsFocused(false)
      }
    }
  }, [isFocused, isTabletOrSmaller, updateModalConfig])

  const handleFocus = useCallback(() => {
    if (!isFocused) {
      setIsFocused(true)
      Analytics.trackClientEvent({
        action: 'click',
        subject: 'open_map_search',
        category: AnalyticsPage.tripPlanner,
        type: 'interaction',
      })
    }
  }, [isFocused])

  const isFullScreen = isTabletOrSmaller && isFocused

  const onPlaceSelect = useCallback((option: ExploreItem) => {
    if (inputRef?.current) {
      inputRef.current.value = option.name
    }
    setIsFocused(false)
    setMapSearchItem(option)

    setSearchPhrase(option.name)
  }, [setMapSearchItem])

  // BaseAutoCompleteInput has its own function for determining when to blur
  // (and it only triggers when it has items in the dropdown i.e. > 2 char in the input)
  // If you trigger an handleBlur/onBlur here as well it re-renders the drop down and you have to click the option you want twice....
  // work around is to let it handle the blurring itself but have separate state for the
  // explore items focused state... we can force disable the blur event in the BaseAutoCompleteInput when in full screen mode

  const [inExploreDropdownContext, setInExploreDropdownContext] =
    useState(false)

  const handleBlur = useCallback(() => {
    // essentially just closing the dropdown
    setIsFocused(false)
  }, [])

  useEffect(() => {
    const currRef = inputRef?.current
    if (currRef) {
      currRef.addEventListener('blur', handleBlur)
    }

    // the blurring is handled in BaseAutoCompleteInput when the search has started
    // We also don't want the blurring effect when in full screen mode.
    if (
      (searchStarted || isFullScreen || inExploreDropdownContext) &&
      currRef
    ) {
      currRef.removeEventListener('blur', handleBlur)
    }

    return () => {
      currRef?.removeEventListener('blur', handleBlur)
    }
  }, [inExploreDropdownContext, isFullScreen, handleBlur, searchStarted])

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPhrase(e.currentTarget.value)
  }, [])

  const onClear = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = ''
    }
    setSearchPhrase('')
  }, [])

  const onEnterPress = useCallback(
    (_predictions: Array<TripGooglePlaceAutocompletePrediction>) => {
      const exploreItem = exploreItems?.[0]
      if (!searchStarted && exploreItem) {
        setMapSearchItem(exploreItem)
        setIsFocused(false)
        onClear()
      }
    },
    [exploreItems, onClear, searchStarted, setMapSearchItem],
  )

  const reFocus = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  return (
    <SearchBarContainer className={cn({ 'full-screen': isFullScreen })}>
      <FormProvider {...methods}>
        <MapSearchAutocomplete
          {...register('place')}
          placeholder="Have something in mind? Find it here"
          autocompleteTypes={AUTOCOMPLETE_TYPES}
          accommodationItems={accommodationItems}
          onPlaceSelect={onPlaceSelect}
          onChange={onChange}
          onFocus={handleFocus}
          onEnterPress={onEnterPress}
          locationBias={searchLocationBias}
          endIcon={<LineSearchIcon />}
          isFullScreen={isFullScreen}
          hasFullScreenMode={isTabletOrSmaller}
          isFocused={isFocused}
          ref={inputRef}
          onClear={onClear}
          searchPhrase={searchPhrase}
          onItemSave={handleBlur}
          onItemDelete={handleBlur}
        />

        {isFocused && !searchStarted && (exploreItems?.length ?? 0) > 0 && (
          <ExploreDropDownContainer
            className={cn({ 'full-screen': isTabletOrSmaller && isFocused })}
            onMouseEnter={() => setInExploreDropdownContext(true)}
            onMouseLeave={() => setInExploreDropdownContext(false)}
            onClick={reFocus}
          >
            <StyledHeading variant="heading6">
              Popular attractions
            </StyledHeading>
            {exploreItems?.slice(0, 5).map((item, idx) => (
              <ListItem
                startIcon={<LineUniversityIcon />}
                key={item.name + idx}
                onClick={(e) => {
                  onPlaceSelect({ ...item })
                  Analytics.trackClientEvent({
                    action: 'click',
                    subject: 'map_search_result',
                    category: AnalyticsPage.tripPlanner,
                    type: 'interaction',
                  })
                  e.stopPropagation()
                }}
                size="medium"
                endIcon={
                  <SearchBookmarkButton
                    exploreItem={item}
                    source="list"
                    onSave={handleBlur}
                    onDelete={handleBlur}
                  />
                }
                title={item.name}
                subtitle={
                  <MapSearchPresentationSelector
                    secondaryText={item.place.region}
                    location={{
                      ...item.place.point,
                      placeId: item.id, // explore item ids are placeIds
                    }}
                    accommodationItems={accommodationItems}
                  />
                }
              />
            ))}
          </ExploreDropDownContainer>
        )}
      </FormProvider>
    </SearchBarContainer>
  )
}

export default MapSearch
